import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { DEFAULTS, DEFAULT_LOCALE, localeData } from "../content/schema";

// Deep-merge saved overrides over the code defaults. Objects recurse; arrays and
// scalars are taken from the override when present. This guarantees the public site
// always renders (defaults) even before anything is saved or if the fetch fails.
function deepMerge(base, override) {
  if (override === undefined || override === null) return base;
  if (Array.isArray(base) || Array.isArray(override)) return override ?? base;
  if (typeof base === "object" && typeof override === "object") {
    const out = { ...base };
    for (const k of Object.keys(override)) out[k] = deepMerge(base[k], override[k]);
    return out;
  }
  return override;
}

const ContentContext = createContext({ content: DEFAULTS, ready: false, preview: false, reload: () => {}, exitPreview: () => {} });

function PreviewBanner({ onExit }) {
  return (
    <div className="fixed bottom-4 left-4 z-[60] flex items-center gap-3 bg-[#B35D4B] text-white pl-4 pr-2 py-2 shadow-lg text-sm" data-testid="preview-banner">
      <span className="font-medium">Preview — showing unpublished draft</span>
      <button onClick={onExit} data-testid="preview-exit" className="bg-white/20 hover:bg-white/30 px-3 py-1 text-xs">Exit preview</button>
    </div>
  );
}

export function ContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULTS);
  const [ready, setReady] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = (isPreview) => {
    api.get("/content", isPreview ? { params: { preview: 1 } } : undefined)
      .then((r) => { setContent(deepMerge(DEFAULTS, localeData(r.data?.data, DEFAULT_LOCALE))); setPreview(!!r.data?.preview); })
      .catch(() => { setContent(DEFAULTS); setPreview(false); })
      .finally(() => setReady(true));
  };

  useEffect(() => {
    const pv = new URLSearchParams(window.location.search).get("preview");
    if (pv === "1") sessionStorage.setItem("cs_preview", "1");
    if (pv === "0") sessionStorage.removeItem("cs_preview");
    const isPreview = sessionStorage.getItem("cs_preview") === "1";
    load(isPreview);
  }, []);

  const exitPreview = () => {
    sessionStorage.removeItem("cs_preview");
    window.location.href = window.location.pathname;
  };

  return (
    <ContentContext.Provider value={{ content, ready, preview, reload: () => load(preview), exitPreview }}>
      {preview && <PreviewBanner onExit={exitPreview} />}
      {children}
    </ContentContext.Provider>
  );
}

// Public hook. Returns the merged content tree plus `p(pageKey)` which always
// returns a defined section object for that page.
export function useContent() {
  const { content, ready, reload, preview, exitPreview } = useContext(ContentContext);
  const p = (pageKey) => content[pageKey] || DEFAULTS[pageKey] || {};
  return { content, ready, reload, preview, exitPreview, p };
}
